import { Injectable } from '@angular/core';
import { CommunicationService } from '../../../services/communication.service';
import {
  BaseDataObject,
  MeterDataObject,
  OldMeterData,
  PowerFacilityData,
  PowerFacilityResponse,
  PowerMeterData,
  PowerSpecialConsumerData,
} from './../../facilities-form/facilities-form.domain';

import { Observable } from 'rxjs';
import { UrlSegments } from '../../jurisdiction.constants';
import { map } from 'rxjs/operators';
import { EnvironmentService } from '../../../services/environment.service';
import { CommonService } from '../../../services/common.service';
import { BaseFacilityService } from './base-facility.service';

@Injectable({
  providedIn: 'root',
})
export class PowerFacilityService extends BaseFacilityService {
  constructor(communicationService: CommunicationService, private environment: EnvironmentService, private commonService: CommonService) {
    super(communicationService, {
      UpdateInstallationUrl: UrlSegments.UpdatePowerInstallation,
      UpdateFacilityUrl: UrlSegments.UpdatePowerFacility,
      UpdateDeviceUrl: UrlSegments.UpdatePowerSpecialConsumer,
      CreateDeviceUrl: UrlSegments.CreatePowerSpecialConsumer,
      UpdateMeterUrl: UrlSegments.UpdatePowerMeter,
      CreateMeterUrl: UrlSegments.CreatePowerMeter,
      UpdateOldMeterUrl: UrlSegments.UpdateOldPowerMeter,
      CreateOldMeterUrl: UrlSegments.CreateOldPowerMeter,
    });
  }

  protected createFacility(request: any): Observable<string> {
    return this.communicationService
      .postData<PowerFacilityResponse>(UrlSegments.CreatePowerFacility, request)
      .pipe(map(response => response.powerfacility.find(() => true).facility_id));
  }

  protected deviceFilter(device: any): boolean {
    return device?.specialAppliance;
  }

  protected getDevices(facility: BaseDataObject): any[] {
    return facility.specialAppliance || [];
  }

  protected mapToFacilityRequest(facility: BaseDataObject): PowerFacilityData {
    return {
      commercial_sector: facility.sector,
      registration_no: facility.registration,
      generation_storagesystem: facility.storageSystemCheckbox ? '1' : '0',
      generation_withinternalconsumption: facility.internalConsumptionCheckbox ? '1' : '0',
      sk_device_location: facility.placeOfMeter,
      sk_power_facility_type: facility.facilityType,
      transaction_no: facility.noOfConnections,
      other_type_details: facility.otherType,
      other_location_details: facility.otherLocation,
      simultaneously_required_total_power_after_installation_change: +facility.simultaneouslyRequiredTotalPowerAfterInstallationChange,
      meter_location_designation: facility.meterLocationDesignation,
      reason_for_request: facility.reasonForRequest,
      sk_measuring_concept: facility.measuringConcept,
      sk_power_meter_type: facility.typeOfMeter,
    };
  }

  protected mapToFacilityDeviceRequest(data: any): PowerSpecialConsumerData {
    const dataObject: PowerSpecialConsumerData = {
      charger_controllabledevice: data.consumptionDeviceCheckbox ? '1' : '0',
      charger_publicdevice: data.publicDeviceCheckbox ? '1' : '0',
      consumer_count: data.amount,
      type_name: data.typeName,
      manufacturer: data.manufacturer,
      power: this.commonService.parseLocaleFractionsToStandard(data.power),
      sk_special_consumer_type: data.specialAppliance,
      other_type_details: data.other_appliance,
      dev_req_approval: data.deviceRequiresApproval != null ? (data.deviceRequiresApproval === true ? '1' : '0') : null,
      module_type: data.module_type,
      is_controllable_facility: data.is_controllable_facility != null ? (data.is_controllable_facility ? 'true' : 'false') : 'false',
      is_public_facility: data.is_public_facility != null ? (data.is_public_facility ? 'true' : 'false') : 'false',
      zerez_id: data.zerez_id,
      ski_key: data.skiKey,
      date_of_commissioning: data.dateOfCommissioning ? this.commonService.parseDateForSaving(data.dateOfCommissioning) : '',
      consumption_device_control: data.consumptionDeviceControl,
    };

    if (this.environment.showBlockIntervalsField) {
      dataObject.sk_swkn_block_intervals = data.blockIntervals;
    }

    return dataObject;
  }

  protected mapToMeterRequest(data: MeterDataObject): PowerMeterData {
    const dataObject: PowerMeterData = {
      ...(!this.environment.showFloor && { meter_count: data.amount }),
      sk_communication: data.selectComm,
      sk_meter_mount: data.selectMount,
      sk_modern_meter_subtype: data.modernMeterSubtype,
      sk_power_meter_type: data.meterType,
      transducer_converterfactor: data.transformerFactor,
      transducer_customername: data.customerName,
      transducer_customerprovided: data.providedByCustomerCheckbox ? '1' : '0',
      transducer_msbname: data.msbName,
      transducer_msbprovided: data.providedByMsbCheckbox ? '1' : '0',
      sk_estimated_annual_consumption: data.annualConsumption,
      rlm_measurement: data.rlmMeasurement ? '1' : '0',
      sk_mounting_alignment: data.alignment,
      ...(this.environment.showDropdownMeassuringConceptInMeterData && { sk_measuring_concept: data.concept }),
      ...(this.environment.showFloor && { swkn_floor_or_appnr: data.swknFloor }),
      ...(!this.environment.showOldMeterSection && { old_meter_number: data.oldMeterNumber }),
      ...(data.transducerSize && { sk_transducer_size_a: data.transducerSize }),
      ...(this.environment.showMeterMountingAndChangeInstallationAdditionalData && { max_sim_ref_power: data.maxSimRefPower }),
    };

    if (this.environment.showControlUnitAvailableCheckbox) {
      dataObject.swkn_ctrl_unit_available = data.controlUnitAvailableCheckbox ? '1' : '0';
    }

    return dataObject;
  }

  protected mapToOldMeterRequest(data: MeterDataObject): OldMeterData {
    return {
      replaced_meter_no: data.meterNumber,
      replaced_meter_reading: data.meterReading,
      replaced_meter_image_id: data.meterImageId,
      replaced_meter_action: data.meterAction,
    };
  }
}
